import React, { useState, useEffect } from 'react';
import { X, Settings, Check } from 'lucide-react';

const DashboardConfig = ({ isOpen, onClose, onSave, currentConfig }) => {
  const [config, setConfig] = useState({
    metrics: {
      totalImpressions: true,
      totalSpend: true,
      totalClicks: true,
      totalConversions: true,
      totalRevenue: true,
      avgCTR: true,
      avgCPC: true,
      avgROAS: true,
      avgCPA: true,
      avgCVR: true
      // Removed reach and frequency as they don't exist in the database
    }
  });

  // Available metrics with descriptions
  const availableMetrics = {
    totalImpressions: { label: 'Total Impressions', description: 'Total number of ad impressions' },
    totalSpend: { label: 'Total Spend', description: 'Total advertising spend' },
    totalClicks: { label: 'Total Clicks', description: 'Total number of clicks' },
    totalConversions: { label: 'Total Conversions', description: 'Total number of conversions' },
    totalRevenue: { label: 'Total Revenue', description: 'Total revenue generated' },
    avgCTR: { label: 'Average CTR', description: 'Average click-through rate' },
    avgCPC: { label: 'Average CPC', description: 'Average cost per click' },
    avgROAS: { label: 'Average ROAS', description: 'Average return on ad spend' },
    avgCPA: { label: 'Average CPA', description: 'Average cost per acquisition' },
    avgCVR: { label: 'Average CVR', description: 'Average conversion rate' }
    // Removed reach and frequency as they don't exist in the database
  };

  useEffect(() => {
    if (currentConfig) {
      setConfig(currentConfig);
    }
  }, [currentConfig]);

  const toggleMetric = (metric) => {
    setConfig(prev => ({
      ...prev,
      metrics: {
        ...prev.metrics,
        [metric]: !prev.metrics[metric]
      }
    }));
  };

  const handleSave = () => {
    onSave(config);
  };

  const handleReset = () => {
    setConfig({
      metrics: {
        totalImpressions: true,
        totalSpend: true,
        totalClicks: true,
        totalConversions: true,
        totalRevenue: true,
        avgCTR: true,
        avgCPC: true,
        avgROAS: true,
        avgCPA: true,
        avgCVR: true
        // Removed reach and frequency as they don't exist in the database
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Settings className="h-5 w-5 text-purple-400" />
            <h2 className="text-xl font-semibold text-white">Dashboard Configuration</h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Metrics Section */}
        <div className="space-y-4">
          <div className="bg-slate-700/50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-white mb-4">Key Metrics</h3>
            <p className="text-slate-300 text-sm mb-4">
              Select which metrics to display in the dashboard overview
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(availableMetrics).map(([key, metric]) => (
                <div
                  key={key}
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                    config.metrics[key]
                      ? 'bg-purple-500/20 border-purple-500/50 text-white'
                      : 'bg-slate-600/50 border-slate-500/50 text-slate-300 hover:bg-slate-600/70'
                  }`}
                  onClick={() => toggleMetric(key)}
                >
                  <div className="flex-1">
                    <div className="font-medium">{metric.label}</div>
                    <div className="text-xs opacity-75">{metric.description}</div>
                  </div>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    config.metrics[key]
                      ? 'bg-purple-500 border-purple-500'
                      : 'border-slate-400'
                  }`}>
                    {config.metrics[key] && <Check className="h-3 w-3 text-white" />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-600">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
          >
            Reset to Default
          </button>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors"
            >
              Save Configuration
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardConfig; 